import type { CSSProperties } from 'react';
import { getCurrentClientConfig } from '@/config/clients';
import { getClientColorOverrides } from './colorPalettes/index';
import { deepMerge } from '@/utils/deepMerge';

// Use React CSSProperties color type for better type safety with CSS/JSX
type CSSColor = NonNullable<CSSProperties['color']>;

// Recursive type that replaces all string values with CSSColor while preserving object structure
type ReplaceStringWithCSSColor<T> = {
  [K in keyof T]: T[K] extends string
    ? CSSColor
    : T[K] extends Record<string, unknown>
      ? ReplaceStringWithCSSColor<T[K]>
      : T[K];
};

// Define the color palettes structure by inferring from the const and applying the color transformation
type ColorPalettes = ReplaceStringWithCSSColor<typeof colorPalettesConst>;

// Default color palettes - this is the base/fallback
const colorPalettesConst = {
  M3: {
    white: '#FFFFFF',
    black: '#000000',
    neutral: {
      900: '#141414',
    },
    sys: {
      light: {
        primary: '#5069EC',
        'on-primary': '#FFFFFF',
        'primary-container': '#EAECFF',
        'on-primary-container': '#2942C2',
        'primary-fixed': '#192983',
        'on-primary-fixed': '#2C2D38',
        'on-secondary': '#FFFFFF',
        'secondary-container': '#E5EAF0',
        'on-secondary-container': '#374151',
        outline: '#A1B0C3',
        'outline 2': '#BCC6D2',
        'outline 3': '#DCE3EB',
        'outline 4': '#EDEDFA',
        'outline-variant': '#CAC4D0',
        border: '#DFE1E6',
        'card-border': '#D5D5D5',
        background: '#F9F9FB',
        'background 2': '#EFF3F6',
        'on-background': '#1D1B20',
        surface: '#FFFFFF',
        'surface-variant': '#E7E0EC',
        'on-surface': '#1D1B20',
        'on-surface-variant': '#4E5C79',
        'on-error-container': '#852221',
        'surface-container': '#FFF',
      },
      dark: {
        primary: '#768EFF',
        error: '#F2B8B5',
        'on-error-container': '#F9DEDC',
        outline: '#938F99',
      },
    },
    'state-layers': {
      light: {
        primary: {
          'opacity-0.08': '#5069EC2E',
          'opacity-0.12': '#5069EC47',
        },
        error: {
          'opacity-0.08': '#B3261E14',
          'opacity-0.12': '#B3261E1F',
        },
        secondary: {
          'opacity-0.08': '#625B7114',
          'opacity-0.12': '#625B711F',
          'opacity-0.16': '#625B7129',
        },
        'on-primary': {
          'opacity-0.16': '#FFFFFF29',
          'opacity-0.12': '#FFFFFF1F',
          'opacity-0.08': '#2942C2',
        },
        'on-surface': {
          'opacity-0.12': '#1D1B201F',
        },
        'on-primary-fixed': {
          'opacity-0.08': '#21005D14',
        },
        'on-primary-container': {
          'opacity-0.12': '#4F378A1F',
        },
        'on-secondary-container': {
          'opacity-0.08': '#4A445914',
          'opacity-0.12': '#4A44591F',
        },
      },
    },
  },
  State: {
    'on-error': '#FFFFFF',
    info: '#1F86E4',
    'info-background': '#E4F0FC',
    'info-pressed': '#1B6DB8',
    warning: '#D35909',
    'warning-pressed': '#A54100',
    'warning-background': '#FEE8D7',
    error: '#DE4237',
    'error-pressed': '#B31E13',
    'error-background': '#F9DEDC',
    success: '#008933',
    'success-pressed': '#006529',
    'success-background': '#DEFFE7',
    hover: '#E5EAF0',
    selected: '#FCFAFF',
    'selected-hover': '#E9EDFD',
    striped: '#E3E3E4',
  },
  Mortgage: {
    State: {
      Success: '#457D49',
      'Mild Warning': '#CA8912',
      'Light Success': '#8E9832',
      Info: '#5669B7',
      'Condition Warning': '#EC7E29',
      'Condition Critical': '#B31E13',
    },
  },
  POS: {
    State: {
      Success: '#457D49',
      'Mild Warning': '#CA8912',
      'Light Success': '#8E9832',
      Info: '#5669B7',
    },
  },
  Primary: {
    '0': '#EEF6FF',
    '50': '#BCDCFF',
    '300': '#3F8CFF',
  },
  Neutral: {
    '40': '#94A3B8',
    '50': '#797676',
    '500': '#666D80',
    '600': '#4E5766',
    '900': '#0D0D12',
  },
  Text: {
    primary: '#0F172A',
  },
  'Color Selection': {
    Peach: {
      'Peach 50': '#FFF1EC',
      'Peach 100': '#FFE6DE',
      'Peach 200': '#F99F86',
      'Peach 300': '#EC7655',
    },
    Blue: {
      'Blue 50': '#EBF5FF',
      'Blue 100': '#E4F0FC',
      'Blue 200': '#75B5F0',
      'Blue 300': '#3389D8',
    },
    Yellow: {
      'Yellow 50': '#FFF5D9',
      'Yellow 100': '#FCF0CF',
      'Yellow 200': '#F7C364',
      'Yellow 300': '#E69E19',
    },
    Purple: {
      'Purple 50': '#F9EEFF',
      'Purple 100': '#F4E2FF',
      'Purple 200': '#E1B0F0',
      'Purple 300': '#B96CD1',
      'Purple 400': '#4F378B',
    },
    Orange: {
      'Orange 50': '#FFF0E5',
      'Orange 100': '#FEE8D7',
      'Orange 200': '#FCB783',
      'Orange 300': '#FA8C38',
    },
    Azure: {
      'Azure 50': '#E7F9FF',
      'Azure 100': '#D6EFF8',
      'Azure 200': '#6CCCEF',
      'Azure 300': '#3BB1DC',
    },
    Green: {
      'Green 50': '#F2FFF3',
      'Green 100': '#E5F8E6',
      'Green 200': '#85E08B',
      'Green 300': '#60A965',
    },
    Teal: {
      'Teal 50': '#EAFEFA',
      'Teal 100': '#D5F5EF',
      'Teal 200': '#6CDAC8',
      'Teal 300': '#39AC99',
    },
  },
  Gradients: {
    'loan-program': {
      start: '#5DB1FF',
      middle: '#2DCFCF',
      end: '#0D86FF',
    },
    'quickloan-welcome': {
      start: '#0e15a6',
      middle: '#5e65ec',
      end: '#6e75ff',
    },
    'quickloan-stepper-bg': {
      start: '#f3f7ff',
      middle: '#e7f0ff',
      end: '#f9fafc',
    },
  },
  Tooltip: {
    background: 'rgba(255, 255, 255, 0.2)',
    backdropBlur: 'blur(150px)',
    border: '1px solid #2942C233',
    textColor: '#000000',
    arrowBackground: '#FFFFFF',
    arrowBorder: '1px solid #2942C233',
  },
} as const;

/**
 * Get client-specific color palette with overrides applied
 * Merges default colorPalettesConst with client config overrides
 */
const getClientColorPalette = (): ColorPalettes => {
  const clientOverrides = getClientColorOverrides();

  console.log('**Override colors', clientOverrides);
  // Always merge default colors with client-specific overrides
  const mergedPalette = deepMerge(colorPalettesConst, clientOverrides);

  console.log('🎨 Final merged color palette:', {
    hasOverrides: Object.keys(clientOverrides).length > 0,
    overrides: clientOverrides,
    merged: mergedPalette,
  });

  return mergedPalette as ColorPalettes;
};

// Create the merged color palette (default + client overrides)
const colorPalettes: ColorPalettes = getClientColorPalette();

export default colorPalettes;
export type { ColorPalettes };
